/**
 * Mise en forme des statistiques d'une box de scénario.
 *
 * Les 33 opérateurs de l'éditeur partagent aujourd'hui une grille unique
 * (« entrés / passant / stoppés »), alors qu'un déclencheur d'entrée, un filtre,
 * une attente et un envoi ne répondent pas à la même question. Ce module range
 * chaque opérateur dans une **famille de lecture** et en déduit un chiffre
 * principal, une ventilation et des libellés adaptés.
 *
 * Contraintes tenues ici :
 * - aucune requête supplémentaire : tout est dérivé des compteurs déjà reçus
 *   par `fetchWorkflowAnalytics` ;
 * - aucun texte au cas par cas : les libellés sont des clés i18n choisies par la
 *   famille (et, pour l'attente d'une action, par le type de déclencheur). Rien
 *   ne décrit jamais le contenu d'un filtre, que le marchand compose librement.
 */
import type { GlobalStatistics, SendStatistics } from '@/composables/flows/useFlowAnalytics';

/** Famille de lecture d'une box. */
export type OperatorFamily =
  | 'entry' // point d'entrée : une seule question, « combien de déclenchements ? »
  | 'waitAction' // déclencheur en cours de parcours : « combien ont fait l'action ? »
  | 'filter' // filtre de segmentation : « combien correspondent ? »
  | 'reaction' // filtre sur une réaction à un message : « combien ont réagi à temps ? »
  | 'wait' // attente : « combien attendent en ce moment ? »
  | 'split' // répartition aléatoire : « combien ont été répartis ? »
  | 'send' // envoi d'un message : « combien l'ont reçu ? »
  | 'display' // affichage sur le site : « combien l'ont vue ? »
  | 'contact'; // action immédiate sur le contact : « combien traités ? »

/** Canal d'un opérateur d'envoi ou d'affichage, pour choisir les indicateurs. */
export type SendChannel = 'email' | 'sms' | 'push' | 'whatsapp' | 'rcs' | 'popup' | 'embed';

/** Ligne de ventilation affichée sous le chiffre principal. */
export interface BreakdownLine {
  /** Clé i18n du libellé. */
  labelKey: string;
  value: number;
  /** Rôle visuel : gris pour un aboutissement normal, rouge pour un point à traiter. */
  tone: 'go' | 'wait' | 'neutral' | 'issue';
  /**
   * `blocked` : personnes qui ne sont pas allées plus loin à cette étape. Elles
   * se comptent à côté du chiffre principal et entrent dans la barre de
   * répartition. `after` : ce qui est arrivé après coup à des personnes déjà
   * comptées dans ce chiffre (désabonnement, rebond…) — les additionner
   * fausserait le total, elles restent hors de la barre.
   */
  group: 'blocked' | 'after';
  /** `support` : la ligne ouvre le canal d'assistance au clic. */
  action?: 'support';
}

/** Tout ce dont la vue a besoin pour rendre une box. */
export interface OperatorPresentation {
  family: OperatorFamily;
  channel?: SendChannel;
  /** Chiffre mis en avant, `null` si la famille n'en a pas. */
  headline: { labelKey: string; value: number; rate?: number } | null;
  breakdown: BreakdownLine[];
  /** Entonnoir du message, uniquement pour `send` et `display`. */
  funnel: Array<{ labelKey: string; value: number; rate?: number }>;
  /**
   * Première part de la barre de répartition quand elle ne peut pas être lue
   * sur le chiffre principal : pour un envoi, le nombre affiché en tête est
   * celui des messages partis, qui ne se compte pas sur la même base que les
   * motifs de blocage. On garde ici ceux qui ont réellement franchi l'étape,
   * pour que la barre totalise toujours les arrivées.
   */
  splitBase?: number;
  /**
   * Commandes et revenus attribués, uniquement pour `send` et `display`.
   * `amount` est transporté tel que l'API l'a renvoyé : une chaîne déjà mise en
   * forme avec la devise côté éditeur, un nombre brut côté API publique. La vue
   * choisit le rendu — le reconvertir ici perdrait le montant dans un cas, la
   * devise dans l'autre.
   */
  revenue: { orders: number; amount: string | number | null } | null;
}

const TRIGGER_OPERATORS = new Set([
  'boxperiodiclaunching',
  'boxabandonpanier',
  'boxstatuscommand',
  'boxregisteronsite',
  'boxsubscribetonewsletter',
  'boxquitsite',
  'boxscrollpage',
  'boxcustomjs',
  'boxsitepasstime',
  'boxvisitpage',
  'boxtriggerinternalevent',
  'boxaudiencesetup',
  'boxchoosebulklist',
]);

/** Filtres portant sur la réaction du contact à un message du scénario. */
const REACTION_FILTERS = new Set(['openemail', 'clicklink', 'filledoutform']);

const SEGMENTATION_FILTERS = new Set([
  'boxcontactseg',
  'boxfiltre_1',
  'boxfiltre_2',
  'boxfiltre_3',
  'boxfiltre_4',
  'boxnavigationtrigger',
]);

const SEND_CHANNELS: Record<string, SendChannel> = {
  boxsendemail: 'email',
  boxsendsms: 'sms',
  boxsendpushnotification: 'push',
  boxsendwhatsapp: 'whatsapp',
  boxsendrcs: 'rcs',
};

const DISPLAY_CHANNELS: Record<string, SendChannel> = {
  boxdisplayfreepopup: 'popup',
  boxdisplayfreemodel: 'embed',
};

/**
 * Verbe affiché pour un déclencheur en cours de parcours. La clé dépend du type
 * de box — et, pour le déclencheur générique, de l'événement choisi.
 */
const WAIT_ACTION_VERBS: Record<string, string> = {
  boxvisitpage: 'visitPage',
  boxscrollpage: 'scrollPage',
  boxsitepasstime: 'sitePassTime',
  boxquitsite: 'quitSite',
  boxabandonpanier: 'abandonCart',
  boxstatuscommand: 'statusCommand',
  boxregisteronsite: 'registerOnSite',
  boxsubscribetonewsletter: 'subscribeNewsletter',
  boxcustomjs: 'customJs',
};

const EVENT_VERBS: Record<string, string> = {
  visit_page: 'visitPage',
  scroll: 'scrollPage',
  pass_time: 'sitePassTime',
  quit_site: 'quitSite',
  abandon_cart: 'abandonCart',
  upsert_order: 'statusCommand',
  register_on_site: 'registerOnSite',
  subscribe_to_newsletter: 'subscribeNewsletter',
};

const PREFIX = 'campaigns.common.operatorAnalytics.byFamily';

/**
 * Range un opérateur dans sa famille de lecture.
 *
 * `isRoot` distingue les deux visages d'un déclencheur : en tête de scénario il
 * compte les entrées, plus bas il matérialise une action attendue du visiteur.
 */
export function getOperatorFamily(operatorType: string, isRoot: boolean): OperatorFamily {
  if (operatorType in SEND_CHANNELS) return 'send';
  if (operatorType in DISPLAY_CHANNELS) return 'display';
  if (operatorType === 'boxwait' || operatorType === 'boxdelaismax') return 'wait';
  if (operatorType === 'boxabtest') return 'split';
  if (operatorType === 'boxlist' || operatorType === 'boxtag') return 'contact';
  if (REACTION_FILTERS.has(operatorType)) return 'reaction';
  if (SEGMENTATION_FILTERS.has(operatorType)) return 'filter';
  if (TRIGGER_OPERATORS.has(operatorType)) return isRoot ? 'entry' : 'waitAction';
  return 'filter';
}

/** Clé i18n du verbe d'un déclencheur en cours de parcours. */
export function getWaitActionVerbKey(operatorType: string, event?: string): string {
  const verb = (event && EVENT_VERBS[event]) || WAIT_ACTION_VERBS[operatorType];
  return `${PREFIX}.waitAction.${verb ?? 'generic'}`;
}

const nb = (stat?: { nb: number | string }): number => {
  if (!stat) return 0;
  const value = typeof stat.nb === 'string' ? Number(stat.nb) : stat.nb;
  return Number.isFinite(value) ? value : 0;
};

const rate = (part: number, total: number): number | undefined => (total > 0 ? (part / total) * 100 : undefined);

const line = (
  labelKey: string,
  value: number,
  tone: BreakdownLine['tone'],
  group: BreakdownLine['group'] = 'blocked',
): BreakdownLine | null => (value > 0 ? {
  labelKey, value, tone, group,
} : null);

/** Ligne d'arrêt sans cause connue : cliquable, elle ouvre l'assistance. */
const support = (labelKey: string, value: number): BreakdownLine | null => {
  const item = line(labelKey, value, 'issue');
  return item ? { ...item, action: 'support' } : null;
};

const keep = (lines: Array<BreakdownLine | null>): BreakdownLine[] => lines.filter((item): item is BreakdownLine => item !== null);

/**
 * Indicateurs pertinents par canal. Une pop-up n'a ni ouverture (toujours égale
 * aux affichages) ni rebond ; un SMS n'a pas d'ouverture ; seules les surfaces
 * on-site produisent des inscriptions.
 */
export function channelSupports(channel: SendChannel | undefined, metric: 'open' | 'click' | 'signup' | 'unsub' | 'bounce'): boolean {
  if (!channel) return false;
  switch (metric) {
    case 'open':
      return channel === 'email' || channel === 'whatsapp';
    case 'click':
      return true;
    case 'signup':
      return channel === 'popup' || channel === 'embed';
    case 'unsub':
      return channel !== 'popup' && channel !== 'embed';
    case 'bounce':
      return channel === 'email';
    default:
      return false;
  }
}

/**
 * Construit la présentation d'une box à partir des compteurs déjà chargés.
 *
 * Les inscriptions sont portées par `send.accountCreations` et
 * `send.newsletterSignups` ; les paramètres explicites permettent de les
 * surcharger. Elles restent deux lignes distinctes : les additionner masquerait
 * lequel des deux gestes la surface obtient réellement.
 */
export function buildOperatorPresentation(params: {
  operatorType: string;
  isRoot: boolean;
  event?: string;
  global: GlobalStatistics | null;
  send: SendStatistics | null;
  accountCreations?: number;
  newsletterSignups?: number;
}): OperatorPresentation {
  const {
    operatorType, isRoot, event, global, send,
  } = params;
  const family = getOperatorFamily(operatorType, isRoot);
  const channel = SEND_CHANNELS[operatorType] ?? DISPLAY_CHANNELS[operatorType];

  const entered = nb(global?.entered);
  const passed = nb(global?.passed);
  const finished = nb(global?.finished);
  const stopped = nb(global?.stopped);
  const paused = nb(global?.paused);
  const pressure = nb(global?.blockedByMarketingPressure);
  const noCredits = nb(global?.blockNoCreditsSms);
  const persistence = nb(global?.blockedByPersistence);
  const maxDelay = nb(global?.blockedByMaxDelay);
  const unknown = nb(global?.internalError);
  const planLimit = nb(global?.blockedByPlanLimit);
  const campaignInactive = nb(global?.campaignInactive);
  const deliverability = nb(global?.blockedByDeliverability);
  const outOfDateRange = nb(global?.outOfDateRange);
  const cartAlreadyProcessed = nb(global?.cartAlreadyProcessed);
  const campaignDisabled = nb(global?.campaignDisabled);

  /**
   * Motifs décidés au niveau de la campagne ou du compte : ils peuvent tomber
   * sur n'importe quelle box, d'où un libellé commun à toutes les familles. La
   * dernière ligne couvre les arrêts dont le moteur n'a pas enregistré la cause
   * — elle renvoie vers l'assistance plutôt que d'exposer un code interne.
   */
  const commonStops = (): Array<BreakdownLine | null> => [
    line(`${PREFIX}.common.blockedByPlanLimit`, planLimit, 'issue'),
    line(`${PREFIX}.common.campaignInactive`, campaignInactive, 'neutral'),
    line(`${PREFIX}.common.blockedByDeliverability`, deliverability, 'issue'),
    line(`${PREFIX}.common.outOfDateRange`, outOfDateRange, 'neutral'),
    line(`${PREFIX}.common.cartAlreadyProcessed`, cartAlreadyProcessed, 'neutral'),
    line(`${PREFIX}.common.campaignDisabled`, campaignDisabled, 'neutral'),
    support(`${PREFIX}.common.unknownReason`, unknown),
  ];

  const presentation: OperatorPresentation = {
    family,
    channel,
    headline: null,
    breakdown: [],
    funnel: [],
    revenue: null,
  };

  if (family === 'send' || family === 'display') {
    const sent = nb(send?.send);
    presentation.splitBase = passed + finished;
    const isDisplay = family === 'display';
    const base = `${PREFIX}.${isDisplay ? 'display' : 'send'}`;

    presentation.funnel.push({ labelKey: `${base}.sent`, value: sent });
    if (channelSupports(channel, 'open')) {
      presentation.funnel.push({ labelKey: `${base}.opened`, value: nb(send?.open_unique), rate: rate(nb(send?.open_unique), sent) });
    }
    presentation.funnel.push({ labelKey: `${base}.clicked`, value: nb(send?.click_unique), rate: rate(nb(send?.click_unique), sent) });
    if (channelSupports(channel, 'signup')) {
      const comptes = params.accountCreations
        ?? (send?.accountCreations ? nb(send.accountCreations) : undefined);
      if (comptes !== undefined) {
        presentation.funnel.push({ labelKey: `${base}.accountCreations`, value: comptes, rate: rate(comptes, sent) });
      }

      const newsletters = params.newsletterSignups
        ?? (send?.newsletterSignups ? nb(send.newsletterSignups) : undefined);
      if (newsletters !== undefined) {
        presentation.funnel.push({ labelKey: `${base}.newsletterSignups`, value: newsletters, rate: rate(newsletters, sent) });
      }
    }

    const amount = send?.sales_amount?.nb;
    presentation.revenue = {
      orders: nb(send?.sales),
      amount: amount === undefined || amount === '' ? null : amount,
    };

    presentation.breakdown = keep([
      line(`${base}.blockedByPressure`, pressure, 'neutral'),
      line(`${base}.blockedByCredits`, noCredits, 'issue'),
      line(`${base}.blockedByPersistence`, persistence, 'issue'),
      line(`${base}.blockedOther`, stopped, 'neutral'),
      ...commonStops(),
      channelSupports(channel, 'unsub') ? line(`${base}.unsub`, nb(send?.unsub), 'neutral', 'after') : null,
      channelSupports(channel, 'bounce') ? line(`${base}.bounce`, nb(send?.bounce), 'issue', 'after') : null,
      channelSupports(channel, 'bounce') ? line(`${base}.spam`, nb(send?.spam), 'issue', 'after') : null,
    ]);

    return presentation;
  }

  if (family === 'entry') {
    presentation.headline = { labelKey: `${PREFIX}.entry.headline`, value: entered };
    return presentation;
  }

  if (family === 'contact') {
    const done = passed + finished;
    presentation.headline = { labelKey: `${PREFIX}.contact.headline`, value: done };
    presentation.breakdown = keep(commonStops());
    return presentation;
  }

  if (family === 'wait') {
    presentation.headline = { labelKey: `${PREFIX}.wait.headline`, value: paused };
    presentation.breakdown = keep([
      line(`${PREFIX}.wait.done`, passed + finished, 'go'),
      line(`${PREFIX}.wait.leftPersistence`, persistence, 'neutral'),
      line(`${PREFIX}.wait.maxDelay`, maxDelay, 'neutral'),
      line(`${PREFIX}.wait.leftOther`, stopped, 'neutral'),
      ...commonStops(),
    ]);
    return presentation;
  }

  const done = passed + finished;

  if (family === 'split') {
    presentation.headline = {
      labelKey: `${PREFIX}.split.headline`,
      value: done,
      rate: rate(done, entered),
    };
    presentation.breakdown = keep([
      line(`${PREFIX}.split.notRouted`, stopped, 'neutral'),
      ...commonStops(),
    ]);
    return presentation;
  }

  if (family === 'filter') {
    presentation.headline = { labelKey: `${PREFIX}.filter.headline`, value: done, rate: rate(done, entered) };
    presentation.breakdown = keep([
      line(`${PREFIX}.filter.pending`, paused, 'wait'),
      line(`${PREFIX}.filter.notMatching`, stopped, 'neutral'),
      line(`${PREFIX}.filter.blockedByPersistence`, persistence, 'issue'),
      ...commonStops(),
    ]);
    return presentation;
  }

  if (family === 'reaction') {
    presentation.headline = { labelKey: `${PREFIX}.reaction.headline`, value: done, rate: rate(done, entered) };
    presentation.breakdown = keep([
      line(`${PREFIX}.reaction.stillInTime`, paused, 'wait'),
      line(`${PREFIX}.reaction.notInTime`, stopped, 'neutral'),
      line(`${PREFIX}.reaction.blockedByPersistence`, persistence, 'issue'),
      ...commonStops(),
    ]);
    return presentation;
  }

  // waitAction
  presentation.headline = {
    labelKey: getWaitActionVerbKey(operatorType, event),
    value: done,
    rate: rate(done, entered),
  };
  presentation.breakdown = keep([
    line(`${PREFIX}.waitAction.notYet`, paused, 'wait'),
    line(`${PREFIX}.waitAction.stopped`, stopped, 'neutral'),
    line(`${PREFIX}.waitAction.blockedByPersistence`, persistence, 'issue'),
    ...commonStops(),
  ]);
  return presentation;
}
